import { clsx } from "clsx"

import type {
  SolarQuoteDto,
  SolarSungagePaymentDetailsDto,
} from "~/api/dto/solar-proposal-financing.dto"

import { monthlyUsd, Pill } from "../ProposalSetup/financing/financing-chrome"

export const SUNGAGE_QUOTE_OUT_OF_DATE_LABEL = `Out of date`
export const SUNGAGE_QUOTE_UNAVAILABLE_PROMPT = `Sungage did not quote a monthly payment.`

const percent = (value: number): string => `${value.toFixed(2)}%`

type Row = { label: string; value: string; note?: string }

/**
 * The rows a Sungage Quote reads as, from the server's figures only: initial
 * and continuing payments; BrightStar's deferred portion and the payment it
 * re-amortizes to when that portion isn't paid by the end of the deferred
 * period; Sunrise Escalator's yearly escalation and its last year's payment.
 */
const sungageRows = (details: SolarSungagePaymentDetailsDto): Row[] => {
  const deferredMonths = details.deferredPeriodMonths
  return [
    {
      label: `Initial monthly payment`,
      value: `${monthlyUsd.format(details.initialMonthlyPayment)}/mo`,
      ...(deferredMonths != null
        ? { note: `For the first ${deferredMonths} months` }
        : details.escalatorPercentage != null
          ? { note: `For the first year` }
          : {}),
    },
    {
      label: `Continuing monthly payment`,
      value: `${monthlyUsd.format(details.continuingMonthlyPayment)}/mo`,
    },
    ...(deferredMonths != null
      ? [
          {
            label: `Deferred portion`,
            value: monthlyUsd.format(details.deferredPaymentPortion),
            note: `Paid down by month ${deferredMonths}`,
          },
          {
            label: `Maximum monthly payment`,
            value: `${monthlyUsd.format(details.maxMonthlyPayment)}/mo`,
            note: `If the deferred portion isn't paid by month ${deferredMonths}`,
          },
        ]
      : []),
    ...(details.escalatorPercentage != null
      ? [
          {
            label: `Yearly escalation`,
            value: percent(details.escalatorPercentage),
          },
          {
            label: `Final year monthly payment`,
            value: `${monthlyUsd.format(details.finalMonthlyPayment)}/mo`,
            note: `Year ${details.termYears}`,
          },
        ]
      : []),
  ]
}

/**
 * The Sungage quote Refresh Pricing wrote, for a Sungage plan's Loan Product
 * step. Nothing renders until there is a Sungage Quote; an unavailable one
 * shows the server's reason (a price outside Sungage's range, a deactivated
 * product, or no payment).
 */
export const SolarSungageQuoteCard = ({
  quote,
}: {
  quote: SolarQuoteDto | null
}): JSX.Element | null => {
  const sungage = quote?.sungage
  if (quote == null || sungage == null) return null
  const details = sungage.paymentDetails

  return (
    <section
      data-testid="solar-sungage-quote"
      aria-label={`Sungage ${sungage.fundingGroupLabel} quote`}
      className="rounded-lg border border-bp-border bg-bp-elevated"
    >
      <div className="flex items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
        <span className="text-[length:var(--step--1)] font-semibold text-bp-text">
          {`Sungage ${sungage.fundingGroupLabel}`}
        </span>
        {quote.status === `stale` ? (
          <Pill tone="warn">{SUNGAGE_QUOTE_OUT_OF_DATE_LABEL}</Pill>
        ) : null}
      </div>
      {quote.status === `unavailable` || details == null ? (
        <p
          role="alert"
          className="px-4 py-3 text-[length:var(--step--2)] text-bp-danger"
        >
          {quote.unavailableReason ?? SUNGAGE_QUOTE_UNAVAILABLE_PROMPT}
        </p>
      ) : (
        <table className="w-full text-[length:var(--step--2)]">
          <tbody>
            {sungageRows(details).map((row, index) => (
              <tr
                key={row.label}
                className={clsx(index > 0 && `border-t border-bp-border/60`)}
              >
                <th
                  scope="row"
                  className="px-4 py-2.5 text-left font-medium text-bp-text-muted"
                >
                  {row.label}
                  {row.note ? (
                    <span className="mt-0.5 block font-normal text-bp-text-dim">
                      {row.note}
                    </span>
                  ) : null}
                </th>
                <td className="px-4 py-2.5 text-right tabular-nums text-bp-text">
                  {row.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
